/* eslint-disable no-prototype-builtins */
/* eslint-disable class-methods-use-this */
import { GenerateParamsData } from 'api/interface/GenerateParams.ts';
import { samplersConfig } from 'core/config/samplersConfig';
import { getErrorImgForAspectRatio } from 'core/utils/utils';
import { FeedResponseItem } from '../../api/interface/FeedResponseItem.ts';
import { GeneratedMediaResponse, GenerateResponse } from '../../api/interface/GenerateResponse.ts';
import { MediaType } from '../constants/enums/MediaType.ts';
import { Samplers } from '../constants/enums/SamplersEnum.ts';
import { MediaFormatter } from './MediaFormatter.ts';

export class StudioMediaFormatter extends MediaFormatter {
	getBatchSizeData = (
		batchData: GenerateResponse | FeedResponseItem,
		generateParams?: GenerateParamsData,
	) => {
		if (batchData.hasOwnProperty('params')) {
			return (batchData as FeedResponseItem).params.batch_size;
		}
		return generateParams?.batchSizeInput;
	};

	override getMediaCheckpointSettings = (
		mediaResponse: GeneratedMediaResponse,
		settings?: Pick<
			GenerateParamsData,
			'AIGuidanceInput' | 'samplerInput' | 'quality' | 'samplingStepsInput' | 'selectedLorasMap'
		>,
	) => {
		const { model, style } = mediaResponse;
		const modelData = {
			model: model?.name,
			modelImage: model?.preview_image,
			modelLabel: model?.display_name,
		};
		const styleData = {
			styleId: style?.id,
			styleImage: style?.preview_image,
			styleLabel: style?.display_name,
		};
		if (settings == null) {
			let checkpointData = null;
			if (model) checkpointData = model;
			if (style) checkpointData = style;

			if (checkpointData) {
				return {
					...(model && modelData),
					...(style && styleData),
					steps: checkpointData?.settings?.steps,
					guidance: checkpointData?.settings?.cfg_scale,
					quality: checkpointData?.settings?.quality,
					sampler: Object.values(Samplers).find(
						(sKey) => samplersConfig[sKey]?.name === checkpointData?.settings?.sampler,
					),
				};
			}
			return {};
		}
		if (model?.settings == null || style?.settings == null) {
			return {
				...(model && modelData),
				...(style && styleData),
				steps: settings?.samplingStepsInput,
				guidance: settings?.AIGuidanceInput,
				quality: settings?.quality,
				sampler: settings?.samplerInput,
			};
		}
		return {};
	};

	override getMiscGenerationDetails = (mediaResponse: GeneratedMediaResponse) => {
		const { status, seed, thumbnail, output } = mediaResponse;
		return {
			loading: status === 'pending' || status === 'initiated',
			status,
			seed,
			thumbnail,
			media: status === 'success' ? output : getErrorImgForAspectRatio(1),
		};
	};

	getStudioFormattedGeneratedItems = (
		generateResponse: GenerateResponse,
		generateParams: GenerateParamsData,
	) => {
		const formattedDerivedMedia = {};
		const createdTimeInMilliSeconds = Date.now();
		const createdTimeInSeconds = createdTimeInMilliSeconds / 1000;
		let mediaType: string = MediaType.IMAGE;
		let generationType: string = '';
		const { media: mediaList, generation_id: batchId, credits } = generateResponse;
		let aspectRatioDetails = {};
		let isSingleMediaGeneration = false;
		let isPromptHidden = false;
		let isChangeItMedia = false;
		let isTrueTouchMedia = false;
		let isUpscaleMedia = false;
		let isFaceReplaceMedia = false;
		let isClothSwapMedia = false;
		const formattedMediaList = [];
		const formattedMediaMap = {};
		mediaList.forEach((mediaResponse: GeneratedMediaResponse) => {
			const {
				media_id: mediaId,
				media_type: mediaResponseType,
				generation_type: mediaGenerationType,
				scenes,
				state: musicVideoStates,
			} = mediaResponse;
			if (mediaResponseType !== mediaType) mediaType = mediaResponseType;
			if (mediaGenerationType !== generationType) generationType = mediaGenerationType;
			const {
				isTrueTouch,
				isChangeIt,
				isUpscale,
				isFaceReplace,
				isClothSwap,
				isSingleMediaGeneration: isSingleMediaGenerationValue,
				isPromptHidden: isPromptHiddenValue,
				isImageSettingsHidden,
			} = this.getImageToImageSettings(generateResponse, mediaResponse);
			isTrueTouchMedia = isTrueTouch;
			isChangeItMedia = isChangeIt;
			isUpscaleMedia = isUpscale;
			isFaceReplaceMedia = isFaceReplace;
			isClothSwapMedia = isClothSwap;
			const mediaDimensions = this.getMediaDimensions(mediaResponse);
			aspectRatioDetails = {
				aspectRatio: mediaDimensions.aspectRatio,
				aspectRatioType: mediaDimensions.aspectRatioType,
			};
			isSingleMediaGeneration = isSingleMediaGenerationValue;
			isPromptHidden = isPromptHiddenValue;
			formattedDerivedMedia[mediaId] = {
				generationId: mediaId,
				batchId,
				scenes,
				musicVideoStates,
				generationType: mediaGenerationType,
				data: createdTimeInMilliSeconds,
				...this.getMiscGenerationDetails(mediaResponse),
				...mediaDimensions,
				...this.getMediaCheckpointSettings(mediaResponse, generateParams),
				isChangeIt,
				isTrueTouch,
				isUpscale,
				isFaceReplace,
				isClothSwap,
				...this.getPromptDetails(generateResponse, mediaResponse, generateParams),
				mediaType: mediaResponseType,
				published: mediaResponse.published ?? false,
				media: '',
				attributes: isImageSettingsHidden
					? {}
					: {
							controlNet: this.getControlNetSettings(generateParams?.controlNetOptions),
							faceLock: this.getFacelockSettings(generateParams?.facelockOptions, mediaResponse),
							lipSync: this.getLipSyncSettings(generateParams?.lipSyncOptions, mediaResponse),
							faceSwap: this.getFaceSwapSettings(generateParams?.faceSwapOptions, mediaResponse),
							character: this.getCharacterDetails(mediaResponse, generateParams),
							videoAnyone: this.getVideoAnyoneSettings(
								generateParams?.videoAnyoneOptions,
								mediaResponse,
							),
							textToVideo: this.getTextToVideoSettings(
								generateParams?.textToVideoOption,
								mediaResponse,
							),
						},
			};
			formattedMediaList.push(mediaId);
			formattedMediaMap[mediaId] = {
				batchId,
				mediaId,
			};
		});
		const batchDetails = {
			...aspectRatioDetails,
			batchId,
			batchSize: isSingleMediaGeneration ? 1 : generateParams.batchSizeInput,
			prompt: isPromptHidden ? '' : generateParams.prompt,
			date: createdTimeInSeconds,
			mediaType,
			generationType,
			derivedMedia: formattedDerivedMedia,
			isChangeIt: isChangeItMedia,
			isTrueTouch: isTrueTouchMedia,
			isUpscale: isUpscaleMedia,
			isFaceReplace: isFaceReplaceMedia,
			isClothSwap: isClothSwapMedia,
		};
		return {
			batchDetails,
			batchId,
			credits,
			mediaList: formattedMediaList,
			mediaMap: formattedMediaMap,
		};
	};

	getStudioFormattedFeedItems = (feedItemsResponse: FeedResponseItem[]) => {
		const formattedBatchWiseHistoryChronologicalBatchIdList = [];
		const formattedBatchWiseHistoryMap = {};
		const formattedMediaList = [];
		const formattedMediaMap = {};
		feedItemsResponse.forEach((batch) => {
			const {
				generation_id: batchId,
				created_at: created,
				params: batchParams,
				media: mediaList,
			} = batch;

			mediaList.forEach((mediaResponse) => {
				const {
					media_id: mediaId,
					media_type: mediaType,
					prompt_id: promptId,
					character,
				} = mediaResponse;
				const { attributes } = batchParams;
				const {
					control_net: controlNetSettings,
					facelock: facelockSettings,
					segment: changeItSettings,
					face_swap: faceSwapSettings,
					lip_sync: lipSyncSettings,
					video_anyone: videoAnyoneSettings,
					txt_to_vid: textToVideoSettings,
					generation_type: generationType,
					state: musicVideoStates,
					audio: audioDetails,
				} = attributes;
				const {
					isTrueTouch,
					isChangeIt,
					isUpscale,
					isFaceReplace,
					isImageSettingsHidden,
					isSingleMediaGeneration,
					isPromptHidden,
					isClothSwap,
				} = this.getImageToImageSettings(batch, mediaResponse);
				const mediaDimensions = this.getMediaPlacementDetails(mediaResponse);
				const hasCharacter = character != null && Object.keys(character ?? {}).length > 0;
				const promptDetails = this.getPromptDetails(batch, mediaResponse);
				formattedMediaList.push(mediaId);
				formattedMediaMap[mediaId] = {
					batchId,
					mediaId,
				};
				if (!Object.keys(formattedBatchWiseHistoryMap).includes(`${batchId}`)) {
					formattedBatchWiseHistoryMap[`${batchId}`] = {
						batchId,
						prompt: isPromptHidden ? '' : promptDetails.prompt,
						batchSize: isSingleMediaGeneration ? 1 : this.getBatchSizeData(batch),
						date: created,
						aspectRatio: mediaDimensions.aspectRatio,
						aspectRatioType: mediaDimensions.aspectRatioType,
						mediaType,
						derivedMedia: {},
						isChangeIt,
						isTrueTouch,
						isUpscale,
						isFaceReplace,
						isClothSwap,
						generationType,
					};
					formattedBatchWiseHistoryChronologicalBatchIdList.push(`${batchId}`);
				}
				formattedBatchWiseHistoryMap[`${batchId}`].derivedMedia[`${mediaId}`] = {
					generationId: mediaId,
					batchId,
					data: created,
					musicVideoStates,
					audioDetails,
					...this.getMiscGenerationDetails(mediaResponse),
					...mediaDimensions,
					...this.getMediaCheckpointSettings(mediaResponse),
					isChangeIt,
					isTrueTouch,
					isUpscale,
					isFaceReplace,
					isClothSwap,
					generationType,
					...this.getPromptDetails(batch, mediaResponse),
					mediaType,
					promptId,
					published: mediaResponse.published ?? false,
					...this.getMiscGenerationDetails(mediaResponse),
					attributes: isImageSettingsHidden
						? {}
						: {
								controlNet: this.getControlNetSettings(controlNetSettings),
								faceLock:
									!hasCharacter && this.getFacelockSettings(facelockSettings, mediaResponse),
								lipSync: this.getLipSyncSettings(lipSyncSettings, mediaResponse),
								faceSwap: this.getFaceSwapSettings(faceSwapSettings, mediaResponse),
								character: this.getCharacterDetails(mediaResponse),
								changeIt: this.getChangeItSettings(changeItSettings, batch.params.segment),
								videoAnyone: this.getVideoAnyoneSettings(videoAnyoneSettings, mediaResponse),
								textToVideo: this.getTextToVideoSettings(textToVideoSettings, mediaResponse),
							},
				};
			});
		});
		return {
			mediaList: formattedMediaList,
			mediaMap: formattedMediaMap,
			batchWiseHistoryMap: formattedBatchWiseHistoryMap,
			batchWiseHistoryChronologicalBatchIdList: formattedBatchWiseHistoryChronologicalBatchIdList,
		};
	};
}

/* eslint-enable no-prototype-builtins */
/* eslint-enable class-methods-use-this */
